import React, { useMemo, useState } from 'react';
import { X, Search, Check } from 'lucide-react';
import ProductIcon, { ICON_REGISTRY } from './ProductIcon';

/**
 * Full-screen icon picker modal.
 * Grouped by category, searchable.
 */
const IconPicker = ({ value, onChange, onClose }) => {
  const [q, setQ] = useState('');

  const grouped = useMemo(() => {
    const filter = q.trim().toLowerCase();
    const list = ICON_REGISTRY.filter(
      (i) => !filter || i.key.includes(filter) || i.label.toLowerCase().includes(filter) || i.group.toLowerCase().includes(filter)
    );
    return list.reduce((acc, item) => {
      (acc[item.group] = acc[item.group] || []).push(item);
      return acc;
    }, {});
  }, [q]);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center px-4 py-6 overflow-y-auto">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-[720px] max-h-[85vh] border border-[#1f1f1f] bg-[#0a0a0a] rounded-md flex flex-col font-mono my-auto">
        <div className="px-5 py-4 border-b border-[#151515] flex items-center justify-between">
          <div>
            <div className="text-[10px] tracking-[0.25em] text-[#5a5a5a]">SELECT PRODUCT ICON</div>
            <div className="text-[13px] text-[#e6e6e6] mt-0.5">{ICON_REGISTRY.length} brands available</div>
          </div>
          <button onClick={onClose} className="text-[#5a5a5a] hover:text-[#e6e6e6]"><X size={16} /></button>
        </div>

        <div className="px-5 py-3 border-b border-[#151515]">
          <div className="flex items-center gap-2 border border-[#1f1f1f] rounded-md px-3 py-2 focus-within:border-[#22c55e]">
            <Search size={13} className="text-[#5a5a5a]" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search brand, category, or key…"
              className="flex-1 bg-transparent text-[13px] text-[#e6e6e6] outline-none placeholder:text-[#4a4a4a]"
              autoFocus
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          {Object.keys(grouped).length === 0 && (
            <div className="text-center text-[12px] text-[#5a5a5a] py-8">No icons match &quot;{q}&quot;</div>
          )}
          {Object.entries(grouped).map(([group, items]) => (
            <div key={group}>
              <div className="text-[10px] tracking-[0.3em] text-[#5a5a5a] mb-2">— {group.toUpperCase()}</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                {items.map((it) => {
                  const active = value === it.key;
                  return (
                    <button
                      type="button"
                      key={it.key}
                      onClick={() => { onChange(it.key); onClose(); }}
                      className={`relative flex items-center gap-2 px-2.5 py-2 rounded-md border transition-colors text-left ${
                        active
                          ? 'border-[#22c55e] bg-[#0f1a12]'
                          : 'border-[#151515] bg-[#080808] hover:border-[#2a2a2a]'
                      }`}
                    >
                      <ProductIcon type={it.key} size={14} />
                      <div className="min-w-0">
                        <div className="text-[11px] text-[#e6e6e6] truncate">{it.label}</div>
                        <div className="text-[9px] text-[#5a5a5a] truncate">{it.key}</div>
                      </div>
                      {active && <Check size={12} className="absolute top-1.5 right-1.5 text-[#22c55e]" />}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default IconPicker;
